// ═══════════════════════════════════════════════════════════════
// ActiveJob — the workspace screen you land on when a job is in
// progress. Reachable via /active/:jobId. Auto-navigated to from
// Home / Calendar / per-customer JobCard the moment Start fires.
//
// What's on it:
//   - Live "started X min ago" timer (updates every 30s)
//   - Customer contact actions (call/text/email + maps link)
//   - Original job description / vehicle / estimate total
//   - "What you did" textarea — autosaves to job.workSummary (~800ms debounce)
//   - "Extra parts" textarea — autosaves to job.extraParts
//   - Big Leave button (opens existing JobLeaveModal — already
//     pre-fills from what you typed here) + small Undo Start
//
// Auto-bounce conditions: if the job doesn't exist, isn't started,
// gets closed elsewhere, or moves to awaiting-return state, this
// screen is no longer relevant — navigate the user away.
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft, Phone, MessageSquare, Mail, MapPin, Car, Clock,
  LogOut as LeaveIcon, Undo2, ChevronRight, Search,
} from 'lucide-react';
import { jobsCol } from './database.js';
import { updateOne } from './lib/db-crud.js';
import { useUserData } from './lib/use-user-data.js';
import { useBusinessProfile } from './lib/use-business-profile.js';
import {
  isTerminal, formatPrice, formatPhone, toDate,
} from './lib/job-helpers.js';
import { vehicleLabel } from './Vehicles.jsx';
import JobLeaveModal from './JobLeaveModal.jsx';
import JobPhotosPanel from './JobPhotosPanel.jsx';
import InspectionPanel from './InspectionPanel.jsx';
import PartSearchPicker, { partSearchQueryFromText } from './PartSearchPicker.jsx';

// Re-render every 30s so the elapsed-minutes label updates without
// the user having to refresh.
function useNowTick(intervalMs = 30_000) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
}

function elapsedLabel(startedAt) {
  const start = toDate(startedAt);
  if (!start) return null;
  const min = Math.max(0, Math.round((Date.now() - start.getTime()) / 60_000));
  if (min === 0) return 'Just started';
  if (min === 1) return 'Started 1 min ago';
  if (min < 60) return `Started ${min} min ago`;
  const hr = Math.floor(min / 60);
  const rem = min % 60;
  if (hr === 1 && rem === 0) return 'Started 1 hr ago';
  if (rem === 0) return `Started ${hr} hr ago`;
  return `Started ${hr} hr ${rem} min ago`;
}

export default function ActiveJob() {
  const { jobId } = useParams();
  const navigate  = useNavigate();
  const { jobs, allCustomers, vehicles, ready } = useUserData();
  const { profile: businessProfile }         = useBusinessProfile();
  useNowTick();

  const job      = useMemo(() => jobs.find((j) => j.id === jobId) || null, [jobs, jobId]);
  // Full set, not the active one: a job already open in the workspace
  // must keep showing whose it is even if the customer was archived.
  const customer = useMemo(() => (job ? allCustomers.find((c) => c.id === job.customerId) : null), [job, allCustomers]);
  const vehicle  = useMemo(() => (job ? vehicles.find((v) => v.id === job.vehicleId) : null), [job, vehicles]);

  const [workSummary, setWorkSummary] = useState('');
  const [extraParts,  setExtraParts]  = useState('');
  const [odometerIn,  setOdometerIn]  = useState('');
  const [odometerOut, setOdometerOut] = useState('');
  const [savedFlash,  setSavedFlash]  = useState(null); // field name | null
  const [leaveOpen,   setLeaveOpen]   = useState(false);
  const [partSearchOpen, setPartSearchOpen] = useState(false);

  // Hydrate the textareas the first time the job loads (and only the first
  // time per job ID — we don't want every Firestore snapshot to wipe what
  // the user is currently typing).
  useEffect(() => {
    if (!job) return;
    setWorkSummary(job.workSummary || '');
    setExtraParts(job.extraParts || '');
    setOdometerIn(job.odometerIn != null ? String(job.odometerIn) : '');
    setOdometerOut(job.odometerOut != null ? String(job.odometerOut) : '');
    // Intentionally only depend on job.id — re-hydrating on every snapshot
    // would clobber in-flight edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);

  // Debounced autosave for workSummary. Skip if value matches what's
  // already in Firestore (avoids redundant writes on hydration).
  useEffect(() => {
    if (!job) return;
    if (workSummary === (job.workSummary || '')) return;
    const id = setTimeout(async () => {
      try {
        await updateOne(jobsCol(), job.id, { workSummary });
        setSavedFlash('workSummary');
        setTimeout(() => setSavedFlash((f) => (f === 'workSummary' ? null : f)), 1200);
      } catch (e) {
        console.error('Autosave workSummary failed:', e);
      }
    }, 800);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workSummary]);

  useEffect(() => {
    if (!job) return;
    if (extraParts === (job.extraParts || '')) return;
    const id = setTimeout(async () => {
      try {
        await updateOne(jobsCol(), job.id, { extraParts });
        setSavedFlash('extraParts');
        setTimeout(() => setSavedFlash((f) => (f === 'extraParts' ? null : f)), 1200);
      } catch (e) {
        console.error('Autosave extraParts failed:', e);
      }
    }, 800);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraParts]);

  useEffect(() => {
    if (!job) return;
    const val = odometerIn.trim() === '' ? null : Number(odometerIn);
    const cur = job.odometerIn != null ? Number(job.odometerIn) : null;
    if (val === cur || (val != null && isNaN(val))) return;
    const id = setTimeout(async () => {
      try {
        await updateOne(jobsCol(), job.id, { odometerIn: val });
        setSavedFlash('odometerIn');
        setTimeout(() => setSavedFlash((f) => (f === 'odometerIn' ? null : f)), 1200);
      } catch (e) {
        console.error('Autosave odometerIn failed:', e);
      }
    }, 800);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [odometerIn]);

  useEffect(() => {
    if (!job) return;
    const val = odometerOut.trim() === '' ? null : Number(odometerOut);
    const cur = job.odometerOut != null ? Number(job.odometerOut) : null;
    if (val === cur || (val != null && isNaN(val))) return;
    const id = setTimeout(async () => {
      try {
        await updateOne(jobsCol(), job.id, { odometerOut: val });
        setSavedFlash('odometerOut');
        setTimeout(() => setSavedFlash((f) => (f === 'odometerOut' ? null : f)), 1200);
      } catch (e) {
        console.error('Autosave odometerOut failed:', e);
      }
    }, 800);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [odometerOut]);

  // Bounce away if the workspace no longer applies
  // elsewhere, scheduled for return, or undone.
  useEffect(() => {
    if (!ready || !job) return;
    if (job.closedAt || job.awaitingReturn) {
      // Work is done OR we've left to come back later — workspace
      // is no longer relevant.
      navigate('/', { replace: true });
    } else if (!job.startedAt) {
      // Either the user hit Undo Start, or they navigated here for a
      // job that hasn't been started. Bounce to the customer page so
      // they can tap Start there if they want.
      navigate(`/customers/${job.customerId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, job?.startedAt, job?.closedAt, job?.awaitingReturn]);

  // ─── Loading / not-found states ──────────────────────────────
  if (!ready) return <p className="muted small">Loading…</p>;
  if (!job)   return <Navigate to="/" replace />;
  // Covers cancelled too — a job killed from another device shouldn't
  // leave a tech sitting on a workspace for work that isn't happening.
  if (isTerminal(job)) return <Navigate to="/history" replace />;

  // ─── Render ─────────────────────────────────────────────────
  const phoneDigits = String(customer?.phone || '').replace(/\D/g, '');
  const mapsUrl = customer?.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(customer.address)}`
    : null;
  const elapsed = elapsedLabel(job.startedAt);

  async function handleUndoStart() {
    try {
      await updateOne(jobsCol(), job.id, { startedAt: null });
      // The bounce-away effect will navigate us out, but be explicit
      // here too in case of any race.
      navigate(`/customers/${job.customerId}`, { replace: true });
    } catch (e) {
      alert(`Could not undo: ${e.message}`);
    }
  }

  return (
    <div className="active-job">
      <div className="active-job-topbar">
        <Link to={`/customers/${job.customerId}`} className="btn-link">
          <ArrowLeft size={14} /> Customer
        </Link>
        <span className="active-job-eyebrow">
          <span className="active-job-pulse" aria-hidden /> ON THE JOB
        </span>
      </div>

      <h1 className="active-job-title">{customer?.name || 'Customer'}</h1>
      {elapsed && (
        <div className="active-job-timer">
          <Clock size={15} /> {elapsed}
        </div>
      )}

      {/* Customer contact card */}
      {customer && (phoneDigits || customer.email || customer.address) && (
        <section className="active-job-card">
          {phoneDigits && (
            <div className="contact-line">
              <span className="contact-text mono">{formatPhone(customer.phone)}</span>
              <div className="contact-actions">
                <a
                  href={`tel:${phoneDigits}`}
                  className={`contact-action ${customer.preferredContact?.includes('phone') ? 'preferred' : ''}`}
                  title={customer.preferredContact?.includes('phone') ? 'Call (preferred)' : 'Call'}
                  aria-label="Call customer"
                >
                  <Phone size={16} />
                </a>
                <a
                  href={`sms:${phoneDigits}`}
                  className={`contact-action ${customer.preferredContact?.includes('text') ? 'preferred' : ''}`}
                  title={customer.preferredContact?.includes('text') ? 'Text (preferred)' : 'Text'}
                  aria-label="Text customer"
                >
                  <MessageSquare size={16} />
                </a>
              </div>
            </div>
          )}
          {customer.email && (
            <div className="contact-line">
              <span className="contact-text">{customer.email}</span>
              <div className="contact-actions">
                <a
                  href={`mailto:${customer.email}`}
                  className={`contact-action ${customer.preferredContact?.includes('email') ? 'preferred' : ''}`}
                  title={customer.preferredContact?.includes('email') ? 'Email (preferred)' : 'Email'}
                  aria-label="Email customer"
                >
                  <Mail size={16} />
                </a>
              </div>
            </div>
          )}
          {mapsUrl && (
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="hero-address">
              <MapPin size={14} />
              <span>{customer.address}</span>
              <ChevronRight size={14} className="hero-address-chevron" />
            </a>
          )}
        </section>
      )}

      {/* Original job ref */}
      <section className="active-job-card">
        <h2 className="active-job-card-title">The job</h2>
        {vehicle && (
          <div className="active-job-meta">
            <Car size={14} /> {vehicleLabel(vehicle)}
          </div>
        )}
        <p className="active-job-desc">{job.description}</p>
        <div className="active-job-est">
          <span className="muted small">Estimate total</span>
          <span className="mono active-job-est-value">{formatPrice(job.totalPrice)}</span>
        </div>
      </section>

      {/* Always available. The inspection used to be gated behind the
          "Inspection" labor preset being on the job, which had the flow
          backwards — a tech inspects the car and builds the estimate
          from what they find, so the checklist has to come first. */}
      <InspectionPanel job={job} vehicle={vehicle} />

      <JobPhotosPanel job={job} />

      <section className="active-job-card">
        <div className="active-job-card-title-row">
          <h2 className="active-job-card-title">Mileage (optional)</h2>
          {(savedFlash === 'odometerIn' || savedFlash === 'odometerOut') && (
            <span className="active-job-saved">✓ Saved</span>
          )}
        </div>
        <div className="form-row-pair">
          <label className="form-row">
            <span>Odometer in</span>
            <input
              type="number" min={0} inputMode="numeric"
              value={odometerIn}
              onChange={(e) => setOdometerIn(e.target.value)}
              placeholder="e.g., 84210"
            />
          </label>
          <label className="form-row">
            <span>Odometer out</span>
            <input
              type="number" min={0} inputMode="numeric"
              value={odometerOut}
              onChange={(e) => setOdometerOut(e.target.value)}
              placeholder="e.g., 84215"
            />
          </label>
        </div>
        {odometerIn && odometerOut && Number(odometerOut) >= Number(odometerIn) && (
          <p className="muted small">
            Trip: {Number(odometerOut) - Number(odometerIn)} mi
          </p>
        )}
      </section>

      {/* Live notes — autosave */}
      <section className="active-job-card">
        <div className="active-job-card-title-row">
          <h2 className="active-job-card-title">What you did</h2>
          {savedFlash === 'workSummary' && <span className="active-job-saved">✓ Saved</span>}
        </div>
        <textarea
          className="active-job-textarea"
          rows={5}
          value={workSummary}
          onChange={(e) => setWorkSummary(e.target.value)}
          placeholder="Take notes as you work — replaced battery, found bad alternator, customer had questions about timing belt, etc."
        />
        <p className="muted small" style={{ margin: '4px 0 0' }}>
          Auto-saves as you type. These notes pre-fill the close-out when you Leave.
        </p>
      </section>

      <section className="active-job-card">
        <div className="active-job-card-title-row">
          <h2 className="active-job-card-title">Extra parts not in the estimate</h2>
          {savedFlash === 'extraParts' && <span className="active-job-saved">✓ Saved</span>}
        </div>
        <textarea
          className="active-job-textarea"
          rows={3}
          value={extraParts}
          onChange={(e) => setExtraParts(e.target.value)}
          placeholder="Battery terminals — $15&#10;Serpentine belt — $42"
        />
        <div className="active-job-part-search-row">
          <button
            type="button"
            className="btn part-lookup-go"
            disabled={!partSearchQueryFromText(extraParts)}
            onClick={() => setPartSearchOpen(true)}
          >
            <Search size={16} /> Search store prices
          </button>
          <span className="muted small">
            Type a part name above, then tap Search to compare prices across stores.
          </span>
        </div>
      </section>

      {/* Bottom action row */}
      <div className="active-job-actions">
        <button
          type="button"
          className="btn hero-action-btn leave"
          onClick={() => setLeaveOpen(true)}
        >
          <LeaveIcon size={16} strokeWidth={2.5} /> Leave
        </button>
        <button
          type="button"
          className="hero-undo-btn"
          onClick={handleUndoStart}
          title="Undo Start (cancel the work timer)"
          aria-label="Undo Start"
        >
          <Undo2 size={16} />
        </button>
      </div>

      {leaveOpen && (
        <JobLeaveModal
          job={job}
          customer={customer}
          businessProfile={businessProfile}
          onClose={() => setLeaveOpen(false)}
        />
      )}

      <PartSearchPicker
        open={partSearchOpen}
        query={partSearchQueryFromText(extraParts)}
        vehicleLabel={vehicle ? vehicleLabel(vehicle) : ''}
        partSearchLinks={businessProfile.partSearchLinks}
        onClose={() => setPartSearchOpen(false)}
      />
    </div>
  );
}
